import type { CreateStringLiteralType } from 'utils/types';

// TODO: import this from declarative UI package
export const SHARING_MODE = {
  ALL: 'all',
  LANGUAGE: 'language',
  LOCALE: 'locale',
  MARKETPLACE: 'marketplace',
  NONE: 'none',
  PLATFORM: 'platform',
  PLATFORM_AND_LANGUAGE: 'platform_and_language',
  PLATFORM_AND_MARKETPLACE: 'platform_and_marketplace',
  STRUCTURE: 'structure',
} as const;

export const SHARING_MODES = Object.values(SHARING_MODE);

export type SharingMode = CreateStringLiteralType<typeof SHARING_MODE>;
export type EffectiveSharingMode = Exclude<SharingMode, 'structure'>;

export type SharingConfig =
  | {
      defaultSharingMode: EffectiveSharingMode;
      otherSharingModes?: EffectiveSharingMode[];
      forcedSharingMode?: EffectiveSharingMode;
      structurePath: never;
    }
  | {
      defaultSharingMode: never;
      otherSharingModes: never;
      forcedSharingMode: 'structure';
      structurePath: string | string[];
    };

/**
 * DMCC Override Map data structure for user-configurable sharing mode overrides.
 *
 * This type mirrors the form data structure with sharing modes as leaf values,
 * enabling granular field-level control over sharing behavior in DMCC campaigns.
 */
export type DMCCOverrideMap = {
  [key: string]: DMCCOverrideMap | DMCCOverrideMap[] | EffectiveSharingMode;
};

/**
 * Configuration management type for DMCC field sharing overrides.
 *
 * Provides CRUD operations for managing user-defined sharing mode overrides
 * at the field level. Uses sparse storage to only persist non-default values.
 */
export type DMCCFieldSharingConfig = {
  /**
   * Reactive override map that automatically updates when form data changes.
   */
  dmccOverrideMap: DMCCOverrideMap | undefined;

  /**
   * Sets a sharing mode override for a specific field path.
   *
   * @param fieldPath - Dot-notation path to the field
   * @param sharingMode - The sharing mode to apply as an override
   */
  setFieldOverride(fieldPathArray: PropertyKey[], sharingMode: EffectiveSharingMode): DMCCOverrideMap;

  /**
   * Removes a sharing mode override for a specific field path.
   *
   * @param fieldPath - Dot-notation path to the field
   */
  removeFieldOverride(fieldPathArray: PropertyKey[]): DMCCOverrideMap;

  /**
   * Sets the complete override map, replacing all existing overrides.
   *
   * @param overrideMap - The new override map to set
   */
  setOverrideMap(overrideMap: DMCCOverrideMap): DMCCOverrideMap;
};
